import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import type { StepDefinition } from "@/shared/components/common/PropertyStepperScreen";
import { routes, type SelectPropertyTypeLocationState } from "@/app/navigation/routes";
import { useAuth } from "@/app/providers/useAuth";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useAddress } from "@/shared/hooks/useAddress";
import { useAddressListViewModel } from "@/shared/components/common/AddressSelectPill/AdrressListviewmodel";
import { getLocalSupportingDocuments } from "@/features/protest/services/localSupportingDocuments.service";
import { useHomePageViewModal } from "@/features/home/viewModal/homePageViewModal";
import { propertyService } from "@/features/properties/model/propertyServices";
import { isValidUUID } from "@/features/properties/model/guestSession";
import type { PropertyApiResponse } from "@/features/properties/model/propertiesTypes";
import { tokenService } from "@/services/auth/token.service";
export type PropertyType = "RESIDENTIAL" | "COMMERCIAL" | "LAND_OR_LOT" | "LAND_LOT";

type DocItem = {
  id: string;
  title: string;
  iframeSrc: string;
  kind: "preview" | "signed";
  isComplete?: boolean;
  desc?: string;
};

function getUiPropertyTypeFromApi(property: PropertyApiResponse | undefined): PropertyType | null {
  const raw = [property?.property_type, property?.property_raw_type].filter(Boolean).join(" ");
  if (!raw) return "RESIDENTIAL";
  const normalized = raw.toLowerCase();
  if (normalized.includes("commercial")) return "COMMERCIAL";
  if (normalized.includes("land") || normalized.includes("lot")) return "LAND_OR_LOT";
  return "RESIDENTIAL";
}

function getPreviewUrl(docType: string): string | null {
  let baseUrl;
  switch (docType) {
    case "AOA":
      baseUrl = "https://drive.google.com/file/d/1zFMwpNw3G3GeYR2tD4-zAY7u-7shtfsh";
      break;
    case "DTR":
      baseUrl = "https://drive.google.com/file/d/1GMoYrtwSAoo4P77LGguYWDBNFE7BHWFj";
      break;
    case "HOMESTEAD":
      baseUrl = "https://drive.google.com/file/d/1oIuAjP2cSGm1nugX0e9cBmx68E_JHy1Y";
      break;
    case "AGREEMENT":
      baseUrl = "https://drive.google.com/file/d/1aNnLRmXR0nUpCJ8_uNU8PKx3L42ajqDZ";
      break;
    case "NOTICE":
      baseUrl = "https://drive.google.com/file/d/1hV1Mwf_VeXA7JGNAkXf3mPeXAFq_U0dF";
      break;
    default:
      return null;
  }
  return `${baseUrl}/preview`;
}

type ApiHomesteadExemptionDoc = {
  document_url?: string | null;
  homestead_document_url?: string | null;
  exemption_document_url?: string | null;
  status?: string | null;
};

type ApiPropertyProtestDoc = {
  signed_aoa?: string | null;
  signed_dtr?: string | null;
  signed_dtr_document_url?: string | null;
  signed_andy_app_agreement?: boolean | null;
  signed_andy_app_agreement_document_url?: string | null;
  signed_notice_of_protest?: boolean | null;
  signed_notice_of_protest_document_url?: string | null;
};

export const useSelectPropertyTypePageViewModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const homeVm = useHomePageViewModal({ skipCommunityData: true });
  const { selectedAddress } = useAddress();

  // Read paidPropertyIds and guest state from router state or localStorage
  const navState = location.state as
    | (SelectPropertyTypeLocationState & {
        paidProperties?: PropertyApiResponse[];
      })
    | null
    | undefined;

  const [guestProperties, setGuestProperties] = useState<PropertyApiResponse[]>(
    () => (navState?.paidProperties as PropertyApiResponse[]) || []
  );
  const [isGuestLoading, setIsGuestLoading] = useState<boolean>(false);

  useEffect(() => {
    if (guestProperties.length > 0) return;
    let targetIds: string[] = [];
    if (navState?.paidPropertyIds && navState.paidPropertyIds.length > 0) {
      targetIds = navState.paidPropertyIds;
    } else {
      try {
        const stored = localStorage.getItem("protest_paid_property_ids");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) targetIds = parsed;
        }
      } catch {
        targetIds = [];
      }
    }

    if (targetIds.length > 0) {
      setIsGuestLoading(true);
      Promise.all(
        targetIds.map(async (id: string) => {
          try {
            return await propertyService.getPropertyById(id);
          } catch (e) {
            console.error("Failed to fetch property details for id", id, e);
            return null;
          }
        })
      )
        .then((props) => {
          setGuestProperties(props.filter((p): p is PropertyApiResponse => Boolean(p)));
        })
        .finally(() => setIsGuestLoading(false));
    }
  }, [navState?.paidPropertyIds, guestProperties.length]);

  const { properties: addressListProperties, isLoading: isAddressListLoading } =
    useAddressListViewModel();

  const storedUser = tokenService.getUser();
  const activeUser = user || storedUser;
  const accountId = activeUser?.id ? String(activeUser.id) : undefined;
  const queryParam = useMemo(() => {
    const sp = new URLSearchParams();
    if (accountId) sp.set("account_id", accountId);
    return sp.toString();
  }, [accountId]);

  const { data: allPropertiesFromQuery, isLoading: isQueryLoading } = useQuery({
    queryKey: [QUERY_KEYS.PROPERTIES, accountId],
    queryFn: () => propertyService.getAll(queryParam),
    enabled: !!accountId,
    refetchOnWindowFocus: false,
  });

  const allProperties = useMemo(() => {
    if (allPropertiesFromQuery && allPropertiesFromQuery.length > 0) {
      return allPropertiesFromQuery;
    }
    if (addressListProperties && addressListProperties.length > 0) {
      return addressListProperties;
    }
    if (guestProperties.length > 0) {
      return guestProperties;
    }
    return allPropertiesFromQuery || addressListProperties || guestProperties || [];
  }, [allPropertiesFromQuery, addressListProperties, guestProperties]);

  const isAllPropertiesLoading =
    (!allProperties || allProperties.length === 0) &&
    (isQueryLoading || isAddressListLoading || isGuestLoading);

  console.log(allProperties, "allProperties3231212");

  const filteredProperties = useMemo(() => {
    if (!allProperties || allProperties.length === 0) return [];

    // Prioritize specifically passed paidPropertyIds from navState or localStorage:
    let targetIds = navState?.paidPropertyIds;
    if (!targetIds || targetIds.length === 0) {
      try {
        const stored = localStorage.getItem("protest_paid_property_ids");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) targetIds = parsed;
        }
      } catch {
        targetIds = undefined;
      }
    }

    if (targetIds && targetIds.length > 0) {
      const idSet = new Set(targetIds.map((id) => String(id)));
      const matched = allProperties.filter(
        (p) =>
          idSet.has(String(p.property_id)) ||
          idSet.has(String((p as unknown as { id?: string | number })?.id ?? ""))
      );
      if (matched.length > 0) return matched;
      if (guestProperties.length > 0) return guestProperties;
    }

    // Filter strictly for properties with payment_status: "paid" and protest_status: "not_filed"
    const paidAndNotFiled = allProperties.filter((p) => {
      const paymentStatus = (p.payment_status || "").toLowerCase().trim();
      const isPaid = paymentStatus === "paid";
      const protestStatus = (p.protest_status || "")
        .toLowerCase()
        .trim()
        .replace(/[\s-]+/g, "_");
      const isNotFiled =
        !protestStatus ||
        protestStatus === "not_filed" ||
        protestStatus === "unfiled" ||
        protestStatus === "none";
      return isPaid && isNotFiled;
    });

    if (paidAndNotFiled.length > 0) {
      return paidAndNotFiled;
    }

    const anyPaid = allProperties.filter(
      (p) => (p.payment_status || "").toLowerCase().trim() === "paid"
    );
    if (anyPaid.length > 0) return anyPaid;

    return allProperties;
  }, [allProperties, navState?.paidPropertyIds, guestProperties]);

  const paidPropertyIds = useMemo(() => {
    if (filteredProperties.length > 0) {
      return filteredProperties.map((p) => p.property_id);
    }
    const rawPaidPropertyIds = navState?.paidPropertyIds || [];
    if (rawPaidPropertyIds.length > 0) return rawPaidPropertyIds;
    if (isAllPropertiesLoading) return []; // Wait for loading
    return selectedAddress ? [selectedAddress] : [];
  }, [filteredProperties, navState?.paidPropertyIds, isAllPropertiesLoading, selectedAddress]);

  const selectedPropertyId = navState?.paidPropertyIds?.[0] || paidPropertyIds[0] || "";
  const userId = user?.id != null ? String(user.id) : "";

  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState<Record<string, PropertyType>>(
    {}
  );
  console.log(selectedPropertyTypes, "selectedPropertyTypes323");
  const [activeDoc, setActiveDoc] = useState<DocItem | null>(null);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isWearAndTearStepperOpen, setIsWearAndTearStepperOpen] = useState(false);
  const [wearAndTearCurrentStep, setWearAndTearCurrentStep] = useState(1);
  const [isSupportingDocsModalOpen, setIsSupportingDocsModalOpen] = useState(false);

  const canFetchPropertyDetails = Boolean(
    selectedPropertyId && selectedPropertyId !== "__add__" && isValidUUID(selectedPropertyId)
  );
  const {
    data: propertyDetails,
    isLoading: isPropertyDetailsLoading,
    error: propertyDetailsError,
  } = useQuery({
    queryKey: [QUERY_KEYS.PROPERTY_DETAILS, selectedPropertyId],
    queryFn: () => propertyService.getPropertyById(selectedPropertyId),
    enabled: canFetchPropertyDetails,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    // If no properties are selected, infer from propertyDetails for the primary selected property.
    // In a multi-property scenario, we will let the user select manually, but we can set defaults based on filteredProperties.
    if (Object.keys(selectedPropertyTypes).length > 0) return;

    const defaults: Record<string, PropertyType> = {};
    if (filteredProperties.length > 0) {
      filteredProperties.forEach((p) => {
        const inferred = getUiPropertyTypeFromApi(p) || "RESIDENTIAL";
        defaults[p.property_id] = inferred;
      });
    } else if (propertyDetails) {
      const inferred = getUiPropertyTypeFromApi(propertyDetails) || "RESIDENTIAL";
      if (selectedPropertyId) defaults[selectedPropertyId] = inferred;
    } else if (selectedPropertyId) {
      defaults[selectedPropertyId] = "RESIDENTIAL";
    }

    if (Object.keys(defaults).length > 0) {
      setSelectedPropertyTypes(defaults);
    }
  }, [propertyDetails, filteredProperties, selectedPropertyTypes, selectedPropertyId]);

  const hasOpenedSupportingDocsRef = useRef(false);

  // Home journey "Supporting Documents" lands on this route with intent in `location.state`;
  // open the same supporting-documents stepper used after protest is filed (no payment/sign flow changes).
  useEffect(() => {
    const nav = location.state as SelectPropertyTypeLocationState | null | undefined;
    if (!nav?.openProtestSupportingDocuments) return;
    if (hasOpenedSupportingDocsRef.current) return;

    hasOpenedSupportingDocsRef.current = true;
    setIsSignatureModalOpen(false);
    setIsSupportingDocsModalOpen(false);
    setWearAndTearCurrentStep(1);
    setIsWearAndTearStepperOpen(true);
    if (selectedPropertyId) {
      setSelectedPropertyTypes((prev) => {
        if (prev[selectedPropertyId]) return prev;
        return { ...prev, [selectedPropertyId]: "RESIDENTIAL" };
      });
    }
  }, [location.state, selectedPropertyId]);

  const docs = useMemo<DocItem[]>(() => {
    const all: DocItem[] = [
      { id: "aoa", title: "AOA Form", iframeSrc: getPreviewUrl("AOA") || "", kind: "preview" },
      { id: "dtr", title: "DTR Form", iframeSrc: getPreviewUrl("DTR") || "", kind: "preview" },
      {
        id: "pba",
        title: "Performance Based Agreement",
        iframeSrc: getPreviewUrl("AGREEMENT") || "",
        kind: "preview",
      },
      {
        id: "nop",
        title: "Notice of Protest",
        iframeSrc: getPreviewUrl("NOTICE") || "",
        kind: "preview",
      },
      {
        id: "hs65",
        title: "Homestead/Over 65 Exemption Application",
        iframeSrc: getPreviewUrl("HOMESTEAD") || "",
        kind: "preview",
        desc: "Your official application for homestead and over 65 tax exemptions with the county.",
      },
      {
        id: "efa",
        title: "Exemption Filing Agreement",
        iframeSrc: getPreviewUrl("AGREEMENT") || "",
        kind: "preview",
        desc: "Agreement for filing your exemption application with the appropriate authorities.",
      },
    ];

    const types = Object.values(selectedPropertyTypes);
    const hasResidential = types.includes("RESIDENTIAL");

    if (!hasResidential && types.length > 0) {
      // ONLY commercial or land/lot properties selected
      return all.filter((d) => d.id !== "nop" && d.id !== "hs65");
    }
    // Mixed or residential: show all
    return all;
  }, [selectedPropertyTypes]);

  const signedDocs = useMemo<DocItem[]>(() => {
    const homestead = (propertyDetails?.documents?.homestead_exemption_documents ??
      []) as ApiHomesteadExemptionDoc[];
    const protest = (propertyDetails?.documents?.property_protest_documents ??
      []) as ApiPropertyProtestDoc[];

    const items: DocItem[] = [];

    const homesteadDoc = homestead.at(0);
    const homesteadUrl =
      homesteadDoc?.homestead_document_url ||
      homesteadDoc?.exemption_document_url ||
      homesteadDoc?.document_url ||
      null;
    const protestDoc = protest.at(0);
    const protestCandidates: Array<{
      id: string;
      title: string;
      url?: string | null;
      complete?: boolean;
    }> = [
      {
        id: "signed_aoa",
        title: "AOA Form",
        url: protestDoc?.signed_aoa,
        complete: Boolean(protestDoc?.signed_aoa),
      },
      {
        id: "signed_dtr",
        title: "DTR Form",
        url: protestDoc?.signed_dtr_document_url || protestDoc?.signed_dtr,
        complete: Boolean(protestDoc?.signed_dtr_document_url || protestDoc?.signed_dtr),
      },
      {
        id: "signed_agreement",
        title: "Performance Based Agreement",
        url: protestDoc?.signed_andy_app_agreement_document_url || null,
        complete: Boolean(
          protestDoc?.signed_andy_app_agreement_document_url ||
          protestDoc?.signed_andy_app_agreement
        ),
      },
      {
        id: "signed_notice",
        title: "Notice of Protest",
        url: protestDoc?.signed_notice_of_protest_document_url || null,
        complete: Boolean(
          protestDoc?.signed_notice_of_protest_document_url || protestDoc?.signed_notice_of_protest
        ),
      },
    ];

    const hasAnyProtestSigned = protestCandidates.some((c) => Boolean(c.complete));
    const shouldShowHomesteadRow = Boolean(homesteadDoc) || hasAnyProtestSigned;
    if (shouldShowHomesteadRow) {
      const isComplete =
        Boolean(homesteadUrl) && (homesteadDoc?.status ?? "").toLowerCase() === "completed";
      items.push({
        id: "signed_homestead_exemption",
        title: "Homestead / Over 65 Exemption",
        iframeSrc: homesteadUrl || "",
        kind: "signed",
        isComplete,
      });
    }

    for (const c of protestCandidates) {
      if (!c.complete) continue;
      if (!c.url) continue;
      items.push({
        id: c.id,
        title: c.title,
        iframeSrc: c.url,
        kind: "signed",
        isComplete: true,
      });
    }

    return items;
  }, [
    propertyDetails?.documents?.homestead_exemption_documents,
    propertyDetails?.documents?.property_protest_documents,
  ]);

  const hasSignedDocs = signedDocs.length > 0;

  const residentialSteps = useMemo<StepDefinition[]>(
    () => [
      { id: "intro", label: "Introduction" },
      { id: "wear", label: "Wear and Tear", supportingDocumentType: "PROPERTY_WEAR_AND_TEAR" },
      {
        id: "neighborhood",
        label: "Neighborhood Wear",
        supportingDocumentType: "NEIGHBORHOOD_CONDITION",
      },
      { id: "sympathy", label: "Sympathy Savings", supportingDocumentType: "SYMPATHY_SAVINGS" },
      { id: "review", label: "Review" },
    ],
    []
  );

  const commercialSteps = useMemo<StepDefinition[]>(
    () => [
      { id: "intro", label: "Introduction" },
      { id: "pictures", label: "Pictures", supportingDocumentType: "PROPERTY_PICTURES" },
      { id: "rent-roll", label: "Rent Roll", supportingDocumentType: "RENT_ROLL" },
      {
        id: "income-expense",
        label: "Income Expense",
        supportingDocumentType: "INCOME_EXPENSE_STATEMENT",
      },
      { id: "future", label: "Future Expenditures", supportingDocumentType: "FUTURE_EXPENDITURES" },
      { id: "appraisal", label: "Appraisal", supportingDocumentType: "APPRAISAL" },
      { id: "review", label: "Review" },
    ],
    []
  );

  const [localSupportingDocuments, setLocalSupportingDocuments] = useState(() =>
    getLocalSupportingDocuments(selectedPropertyId)
  );

  const refreshLocalSupportingDocuments = useCallback(() => {
    setLocalSupportingDocuments(getLocalSupportingDocuments(selectedPropertyId));
  }, [selectedPropertyId]);

  useEffect(() => {
    refreshLocalSupportingDocuments();
  }, [refreshLocalSupportingDocuments]);

  const canView = Object.keys(selectedPropertyTypes).length > 0;
  const iframeSrc = activeDoc ? activeDoc.iframeSrc : "";

  // Helper for Stepper (if mixed, default to residential steps, otherwise commercial)
  const isResidential = Object.values(selectedPropertyTypes).includes("RESIDENTIAL");
  const steps = isResidential ? residentialSteps : commercialSteps;
  const currentStepDefinition = steps[wearAndTearCurrentStep - 1];
  const currentStepTitle = currentStepDefinition?.label ?? "Step";
  const maxSteps = isResidential ? 5 : 7;

  const lastStepHasAnySupportingDocuments = useMemo(() => {
    const flowTypes = new Set(
      steps
        .map((s) => s.supportingDocumentType)
        .filter((t): t is NonNullable<typeof t> => Boolean(t))
    );
    return localSupportingDocuments.some((d) => flowTypes.has(d.documentType));
  }, [localSupportingDocuments, steps]);

  useEffect(() => {
    const hasCommercial = Object.values(selectedPropertyTypes).includes("COMMERCIAL");
    const hasResidential = Object.values(selectedPropertyTypes).includes("RESIDENTIAL");
    if (
      hasCommercial &&
      !hasResidential &&
      activeDoc &&
      (activeDoc.id === "nop" || activeDoc.id === "hs65")
    ) {
      setActiveDoc(null);
    }
  }, [selectedPropertyTypes, activeDoc]);

  useEffect(() => {
    if (!activeDoc) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setActiveDoc(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeDoc]);

  useEffect(() => {
    if (!isWearAndTearStepperOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsWearAndTearStepperOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isWearAndTearStepperOpen]);

  const isSelected = useCallback(
    (propertyId: string, type: PropertyType) => {
      if (propertyId && selectedPropertyTypes[propertyId] !== undefined) {
        return selectedPropertyTypes[propertyId] === type;
      }
      if (selectedPropertyId && selectedPropertyTypes[selectedPropertyId] !== undefined) {
        return selectedPropertyTypes[selectedPropertyId] === type;
      }
      const values = Object.values(selectedPropertyTypes);
      if (values.length === 1) {
        return values[0] === type;
      }
      return type === "RESIDENTIAL";
    },
    [selectedPropertyTypes, selectedPropertyId]
  );

  const onSelectPropertyType = useCallback(
    (propertyId: string, type: PropertyType) => {
      setSelectedPropertyTypes((prev) => {
        const next = { ...prev };
        if (propertyId) next[propertyId] = type;
        if (selectedPropertyId) next[selectedPropertyId] = type;
        return next;
      });
    },
    [selectedPropertyId]
  );

  const onOpenDoc = useCallback(
    (doc: DocItem) => {
      if (Object.keys(selectedPropertyTypes).length === 0) return;
      setActiveDoc(doc);
    },
    [selectedPropertyTypes]
  );

  const onCloseDoc = useCallback(() => {
    setActiveDoc(null);
  }, []);

  const onSignature = useCallback(() => {
    if (!canView) return;
    const hasProtestFiled =
      homeVm.protestStatus || propertyDetails?.protest_status === "filed" || hasSignedDocs;
    if (hasProtestFiled) {
      setWearAndTearCurrentStep(1);
      setIsWearAndTearStepperOpen(true);
      setIsSignatureModalOpen(false);
      return;
    }
    setIsSignatureModalOpen(true);
  }, [canView, homeVm.protestStatus, propertyDetails?.protest_status, hasSignedDocs]);

  const onCloseSignatureModal = useCallback(() => {
    setIsSignatureModalOpen(false);
  }, []);

  const onSignatureModalSubmit = useCallback(() => {
    setWearAndTearCurrentStep(1);
    setIsWearAndTearStepperOpen(true);
  }, []);

  const onCloseStepper = useCallback(() => {
    setIsWearAndTearStepperOpen(false);
    navigate(routes.homepage);
  }, [navigate]);

  const onStepperBack = useCallback(() => {
    refreshLocalSupportingDocuments();
    if (wearAndTearCurrentStep <= 1) {
      setIsWearAndTearStepperOpen(false);
      setWearAndTearCurrentStep(1);
      navigate(routes.homepage);
      return;
    }
    setWearAndTearCurrentStep((step) => step - 1);
  }, [refreshLocalSupportingDocuments, navigate, wearAndTearCurrentStep]);

  const onStepperNext = useCallback(() => {
    refreshLocalSupportingDocuments();
    setWearAndTearCurrentStep((step) => {
      if (step >= maxSteps) return step;
      return step + 1;
    });
  }, [maxSteps, refreshLocalSupportingDocuments]);

  const onStepperSkip = useCallback(() => {
    refreshLocalSupportingDocuments();
    setWearAndTearCurrentStep((step) => {
      if (step >= maxSteps) return step;
      return step + 1;
    });
  }, [maxSteps, refreshLocalSupportingDocuments]);

  const onStepperSubmit = useCallback(() => {
    setIsWearAndTearStepperOpen(false);
    setIsSupportingDocsModalOpen(true);
  }, []);

  const onCloseSupportingDocsModal = useCallback(() => {
    setIsSupportingDocsModalOpen(false);
  }, []);

  return {
    userId,
    selectedPropertyId,
    paidPropertyIds,
    propertyDetails,
    isPropertyDetailsLoading,
    propertyDetailsError,
    selectedPropertyTypes,
    docs,
    signedDocs,
    hasSignedDocs,
    canView,
    isSelected,
    onSelectPropertyType,
    onSignature,
    activeDoc,
    onOpenDoc,
    onCloseDoc,
    iframeSrc,
    isSignatureModalOpen,
    onCloseSignatureModal,
    onSignatureModalSubmit,
    isWearAndTearStepperOpen,
    onCloseStepper,
    wearAndTearCurrentStep,
    isResidential,
    currentStepTitle,
    onStepperBack,
    onStepperNext,
    onStepperSkip,
    onStepperSubmit,
    residentialSteps,
    commercialSteps,
    localSupportingDocuments,
    lastStepHasAnySupportingDocuments,
    isSupportingDocsModalOpen,
    onCloseSupportingDocsModal,
    filteredProperties,
    isAllPropertiesLoading,
    navStatePaidPropertyIds: navState?.paidPropertyIds,
  };
};
